import { useState } from 'react';
import { Stack, Text } from '@repletoryreach/sdk-ui';
import { DataTable, StatusBadge, type StatusTone } from '@repletoryreach/sdk-ui/data-display';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import type { insights } from '@repletoryreach/sdk-api';
import { useT } from '../../../i18n/I18nProvider';
import type { Indexed } from '../../../lib/types';
import { MUTED, Muted, Panel, SeeChats, WholeReport } from '../shared';
import type { TabProps } from './tab';

type Improvement = insights.InsightSections['improvements'][number];
const RANK = { high: 0, medium: 1, low: 2 } as const;
const IMPACT_TONE: Record<Improvement['impact'], StatusTone> = { high: 'healthy', medium: 'pending', low: 'inactive' };

/** What to change, most impact and least effort first, filtered by area (chatbot, flows, knowledge, team, hours). */
export function Improvements({ report, onSee }: TabProps) {
  const t = useT();
  const [area, setArea] = useState('all');
  const all = [...(report.sections?.improvements ?? [])]
    .sort((a, b) => RANK[a.impact] - RANK[b.impact] || RANK[b.effort] - RANK[a.effort]);
  const areas = [...new Set(all.map((i) => i.area))];
  const shown = area === 'all' ? all : all.filter((i) => i.area === area);

  if (all.length === 0) return <Muted>{t('insights.improve.none')}</Muted>;
  return (
    <Panel title={t('insights.improve.title')} note={<WholeReport t={t} />} testId="insights-improvements">
      {areas.length > 1 && (
        <div className="rr-insights__scroll-x">
          <SegmentedControl size="xs" value={area} onChange={setArea} data-testid="insights-improve-area"
            data={[{ value: 'all', label: t('insights.improve.allAreas') }, ...areas.map((a) => ({ value: a, label: t(`insights.area.${a}`) }))]} />
        </div>
      )}
      <div className="rr-insights__table-scroll">
        <DataTable<Indexed<Improvement & { key: string }>>
          records={shown.map((x, i) => ({ ...x, key: `${i}-${x.title}` })) as Indexed<Improvement & { key: string }>[]}
          idAccessor="key"
          columns={[
            { accessor: 'title', title: t('insights.improve.what'), render: (x) => (
              <Stack gap={2}>
                <Text role="bodySmall" fw={600}>{x.title}</Text>
                <Text role="bodySmall">{x.detail}</Text>
                {x.evidence && <Text role="caption" c={MUTED}>{t('insights.improve.evidence', { value: x.evidence })}</Text>}
              </Stack>
            ) },
            { accessor: 'area', title: t('insights.improve.area'), render: (x) => <Text role="caption">{t(`insights.area.${x.area}`)}</Text> },
            { accessor: 'impact', title: t('insights.improve.impact'), render: (x) => <StatusBadge status={IMPACT_TONE[x.impact]} size="xs">{t(`insights.level.${x.impact}`)}</StatusBadge> },
            { accessor: 'effort', title: t('insights.improve.effort'), render: (x) => <Text role="caption">{t(`insights.level.${x.effort}`)}</Text> },
            { accessor: 'refs', title: '', render: (x) => <SeeChats refs={x.refs} onSee={onSee} t={t} /> }
          ]}
        />
      </div>
    </Panel>
  );
}
